import React from 'react';
import SEO from '../../components/SEO';

const OurStory = () => {
  return (
    <>
      <SEO 
        title="Our Story - Abacies Logiciels"
        description="Learn about Abacies Logiciels - Founded in 2010, we help organizations turn technology into a strategic advantage through custom digital solutions, AI, and software engineering."
        keywords="Abacies Logiciels, our story, company history, software solutions, AI capabilities, technology partner"
      />

      <section className="hero-section py-10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Our Story
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up">
              Turning technology into a strategic advantage since 2010
            </p>
          </div>
        </div>
      </section>

      {/* About Abacies Section */}
      <section className="py-4 md:py-6 bg-white">
        <div className="page-container">
          <div className="text-justify">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-charcoal">
              About Abacies
            </h2>
            <p className="text-titanium mb-3 leading-relaxed">
              At Abacies Logiciels Pvt. Ltd., we help organizations turn technology into a strategic advantage.
            </p>
            <p className="text-titanium mb-3 leading-relaxed">
              Founded in 2010, Abacies has grown into a trusted software solutions partner for businesses across industries and geographies. We design, build, and scale custom digital solutions that solve real-world business challenges—using the right mix of software engineering, automation, integration, and artificial intelligence.
            </p>
            <p className="text-titanium leading-relaxed">
              Our work is driven by a simple belief: technology should simplify complexity, improve efficiency, and unlock growth.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-4 md:py-6 bg-alabaster">
        <div className="page-container">
          <div className="text-justify">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-charcoal">
              Who We Are
            </h2>
            <p className="text-titanium mb-3 leading-relaxed">
              We are a team of engineers, architects, and solution providers who work closely with our clients to understand their business goals—not just their technical requirements. Over the years, we have partnered with organizations ranging from growing enterprises to established businesses, delivering reliable, scalable, and future-ready solutions.
            </p>
            <p className="text-titanium leading-relaxed">
              Abacies operates with a global mindset, supporting clients around the world while maintaining the agility and responsiveness of a focused technology partner.
            </p>
          </div>
        </div>
      </section>

      {/* Our Strength in AI Section */}
      <section className="py-4 md:py-6 bg-white">
        <div className="page-container">
          <div className="text-justify">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-charcoal">
              Our Strength in AI
            </h2>
            <p className="text-titanium mb-3 leading-relaxed">
              Artificial Intelligence is not new to Abacies—it is a capability we have built and refined over several years.
            </p>
            <p className="text-titanium mb-2 leading-relaxed">
              We leverage AI to:
            </p>
            <ul className="list-disc list-inside space-y-1.5 text-titanium mb-3 ml-4">
              <li>Optimize workflows and reduce manual effort</li>
              <li>Improve accuracy, insights, and response times</li>
              <li>Enable smarter, data-driven business operations</li>
            </ul>
            <p className="text-titanium leading-relaxed">
              Rather than applying AI for its own sake, we focus on real, usable outcomes that deliver value to the business.
            </p>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-4 md:py-6 bg-alabaster">
        <div className="page-container">
          <div className="text-justify">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-charcoal">
              How We Work
            </h2>
            <p className="text-titanium mb-2 leading-relaxed">
              Our approach is collaborative, transparent, and outcome-focused:
            </p>
            <ul className="list-disc list-inside space-y-1.5 text-titanium mb-3 ml-4">
              <li>We listen first to understand business challenges</li>
              <li>We design thoughtfully, keeping scalability and security in mind</li>
              <li>We build with quality, using proven engineering practices</li>
              <li>We support continuously, ensuring long-term success</li>
            </ul>
            <p className="text-titanium leading-relaxed">
              This mindset has helped us build strong, lasting relationships with our clients.
            </p>
          </div>
        </div>
      </section>

      {/* Our Team & Culture Section */}
      <section className="py-4 md:py-6 bg-white">
        <div className="page-container">
          <div className="text-justify">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-charcoal">
              Our Team & Culture
            </h2>
            <p className="text-titanium mb-2 leading-relaxed">
              At the heart of Abacies is a strong team of passionate professionals who are committed to excellence. Since our inception, we have cultivated a culture of:
            </p>
            <ul className="list-disc list-inside space-y-1.5 text-titanium mb-3 ml-4">
              <li>Continuous learning</li>
              <li>Innovation with purpose</li>
              <li>Accountability and ownership</li>
              <li>Long-term client success</li>
            </ul>
            <p className="text-titanium leading-relaxed">
              We believe great software is built by people who care—about the problem, the solution, and the impact it creates.
            </p>
          </div>
        </div>
      </section>

      {/* Looking Ahead Section */}
      <section className="py-4 md:py-6 bg-alabaster">
        <div className="page-container">
          <div className="text-justify">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-charcoal">
              Looking Ahead
            </h2>
            <p className="text-titanium mb-3 leading-relaxed">
              As technology evolves, so do we. Abacies continues to invest in AI, automation, and modern software architectures to help organizations stay competitive in a rapidly changing digital landscape.
            </p>
            <p className="text-titanium leading-relaxed">
              Whether you are modernizing existing systems or building something entirely new, Abacies is your partner in turning ideas into impactful digital solutions.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default OurStory;
